import { z } from 'zod';

import { roleSchema, scopeTypeSchema } from './roles.js';

export const PASSWORD_MIN_LENGTH = 12;

export const emailSchema = z
  .email({ error: 'Email inválido' })
  .max(254)
  .transform((e) => e.trim().toLowerCase());

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, {
    error: `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`,
  })
  .max(256);

export const totpCodeSchema = z.string().regex(/^\d{6}$/, { error: 'El código son 6 dígitos' });
export const recoveryCodeSchema = z
  .string()
  .regex(/^[a-z0-9]{5}-[a-z0-9]{5}$/, { error: 'Código de recuperación inválido' });

/** Estado del segundo factor en la sesión. */
export const mfaStateSchema = z.enum([
  'ok', // sesión completa
  'pending', // falta ingresar el código TOTP
  'enroll_required', // el rol exige 2FA y el usuario no lo configuró: sólo puede enrolarse
]);
export type MfaState = z.infer<typeof mfaStateSchema>;

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.input<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({ mfa: mfaStateSchema });
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/** `rememberDevice`: "Recordar este dispositivo por 30 días" (cookie `__Host-mfa_trust`). */
export const TRUSTED_DEVICE_DAYS = 30;
export const mfaVerifyRequestSchema = z.union([
  z.object({ code: totpCodeSchema, rememberDevice: z.boolean().optional() }),
  z.object({ recoveryCode: recoveryCodeSchema, rememberDevice: z.boolean().optional() }),
]);
export type MfaVerifyRequest = z.infer<typeof mfaVerifyRequestSchema>;

export const mfaEnrollStartResponseSchema = z.object({
  otpauthUrl: z.string(),
  secret: z.string(),
});
export type MfaEnrollStartResponse = z.infer<typeof mfaEnrollStartResponseSchema>;

export const mfaEnrollConfirmRequestSchema = z.object({ code: totpCodeSchema });
export const mfaEnrollConfirmResponseSchema = z.object({
  recoveryCodes: z.array(z.string()),
});
export type MfaEnrollConfirmResponse = z.infer<typeof mfaEnrollConfirmResponseSchema>;

/**
 * Desactivar el 2FA propio (sólo si ninguna regla lo exige): contraseña actual + un código de la app
 * o uno de recuperación.
 */
export const mfaDisableRequestSchema = z.union([
  z.object({ password: z.string().min(1).max(256), code: totpCodeSchema }),
  z.object({ password: z.string().min(1).max(256), recoveryCode: recoveryCodeSchema }),
]);
export type MfaDisableRequest = z.infer<typeof mfaDisableRequestSchema>;

/** Por qué el 2FA es obligatorio para alguien (vacío = opcional). */
export const mfaRequirementReasonSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('superadmin') }),
  z.object({ kind: z.literal('agency') }),
  z.object({ kind: z.literal('client'), clientId: z.uuid(), clientName: z.string() }),
]);
export type MfaRequirementReason = z.infer<typeof mfaRequirementReasonSchema>;

/** Tarjeta "Seguridad" de Mi perfil. */
export const mfaStatusResponseSchema = z.object({
  enabled: z.boolean(),
  enabledAt: z.string().nullable(),
  recoveryCodesLeft: z.number().int(),
  required: z.boolean(),
  reasons: z.array(mfaRequirementReasonSchema),
});
export type MfaStatusResponse = z.infer<typeof mfaStatusResponseSchema>;

/** Mi perfil → Seguridad → "Dispositivos de confianza" (sólo los vigentes). */
export const trustedDeviceSchema = z.object({
  id: z.uuid(),
  /** Resumen del navegador ("Chrome en Windows"), nunca el user-agent entero. */
  userAgent: z.string().nullable(),
  /** /24 (IPv4) o /48 (IPv6). */
  ipPrefix: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string(),
  /** Es el navegador desde el que se está mirando. */
  current: z.boolean(),
});
export type TrustedDevice = z.infer<typeof trustedDeviceSchema>;
export const trustedDeviceListSchema = z.array(trustedDeviceSchema);

export const forgotPasswordRequestSchema = z.object({ email: emailSchema });
export const resetPasswordRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});

export const invitationInfoSchema = z.object({
  email: z.string(),
  scopeType: scopeTypeSchema,
  scopeName: z.string(),
  role: roleSchema,
  expiresAt: z.string(),
});
export type InvitationInfo = z.infer<typeof invitationInfoSchema>;

export const acceptInvitationRequestSchema = z.object({
  token: z.string().min(20).max(200),
  name: z.string().trim().min(1).max(120),
  password: passwordSchema,
});
export type AcceptInvitationRequest = z.infer<typeof acceptInvitationRequestSchema>;

export const createInvitationRequestSchema = z.object({
  email: emailSchema,
  scopeType: scopeTypeSchema,
  scopeId: z.uuid(),
  role: roleSchema,
});
export type CreateInvitationRequest = z.input<typeof createInvitationRequestSchema>;

export const createInvitationResponseSchema = z.object({
  id: z.uuid(),
  expiresAt: z.string(),
});
